<template>
  <div class="outcome-charts pb-5" dir="rtl">
    <PendingTherapyFeedback />
    <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
    <v-btn v-if="error" outlined @click="getAssignRecord">تلاش دوباره</v-btn>
    <v-select v-model="selectedRecord" :items="records" item-value="id" :item-text="doctorLabel"
      label="درمانگر" outlined dense :loading="loadingRecords" @change="getChartData" />
    <div v-if="loadingRecords || localLoader" class="text-center py-6" role="status" aria-label="در حال دریافت بازخورد"><v-progress-circular indeterminate color="primary" /></div>
    <v-alert v-else-if="!error && !records.length" type="info" text>درمانگری برای نمایش بازخورد یافت نشد. دوباره وارد شوید یا صفحه را تازه‌سازی کنید.</v-alert>
    <v-alert v-else-if="!error && !hasScores" type="info" text>هنوز نمره‌ای برای نمایش ثبت نشده است.</v-alert>
    <v-row v-else-if="!error">
      <v-col v-for="instrument in ['ors', 'srs']" :key="instrument" cols="12" md="6">
        <section class="outcome-charts__panel" :data-instrument="instrument">
          <h2 class="outcome-charts__heading">{{ instrument === 'ors' ? 'روند بازخورد پیش از جلسه (ORS)' : 'بازخورد جلسه (SRS)' }}</h2>
          <div class="outcome-charts__plot" dir="ltr">
            <VueApexCharts class="outcome-charts__chart" type="line" height="360" :options="chartOptions(instrument)" :series="series(instrument)" />
          </div>
        </section>
      </v-col>
    </v-row>
    <p class="outcome-charts__caption">بازهٔ نمره‌ها صفر تا چهل است. بخش‌های بدون پاسخ، نمره ندارند. زمان جلسه‌ها بر اساس محدوده زمانی حساب شما نمایش داده می‌شود.</p>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import PendingTherapyFeedback from '@/components/Feedback/PendingTherapyFeedback.vue'
import { numericScore } from '@/services/therapyFeedback'
import { feedbackNumber, feedbackSessionLabel } from '@/services/therapyFeedbackDisplay'

export default {
  name: 'OutcomeCharts',
  components: { VueApexCharts, PendingTherapyFeedback },
  data: () => ({ records: [], selectedRecord: null, sessions: [], loadingRecords: true, localLoader: false, error: '', requestId: 0 }),
  computed: {
    hasScores() { return this.sessions.some(row => numericScore(row.actualscore) !== null || numericScore(row.srs) !== null) },
    timezone() { return (this.$store.getters.getProfile || {}).tz || 'UTC' }
  },
  mounted() { this.getAssignRecord() },
  methods: {
    doctorLabel(record) {
      const user = record && record.doctor && record.doctor.user
      if (!user) return 'درمانگر'
      return user.full_name || [user.name, user.family].filter(Boolean).join(' ') || 'درمانگر'
    },
    series(instrument) {
      return [{ name: instrument.toUpperCase(), data: this.sessions.map(row => numericScore(row[instrument === 'ors' ? 'actualscore' : 'srs'])) }]
    },
    chartOptions(instrument) {
      const key = instrument === 'ors' ? 'clinicalcutoff' : 'srs_cutoff'
      const row = this.sessions.find(session => numericScore(session[key]) !== null)
      return {
        chart: { type: 'line', fontFamily: 'YekanBakh, main, sans-serif', toolbar: { show: false }, zoom: { enabled: false } },
        xaxis: {
          categories: this.sessions.map(session => feedbackSessionLabel(session.scheduled_at, this.timezone)),
          labels: { rotate: -35, rotateAlways: false, trim: false, hideOverlappingLabels: true, style: { fontSize: '11px' } },
          tooltip: { enabled: false }
        },
        yaxis: { min: 0, max: 40, tickAmount: 4, labels: { formatter: feedbackNumber }, title: { text: 'نمره' } },
        stroke: { curve: 'straight', width: 2 }, markers: { size: 5, showNullDataPoints: false },
        grid: { padding: { top: 10, right: 18, left: 8, bottom: 8 } },
        tooltip: { y: { formatter: value => value == null ? 'بدون پاسخ' : `${feedbackNumber(value)} از ۴۰` } },
        colors: [instrument === 'ors' ? '#2c956a' : '#4361a6'],
        annotations: { yaxis: row ? [{
          y: Number(row[key]), borderColor: '#84938e', strokeDashArray: 4,
          label: {
            position: 'left', textAnchor: 'start', offsetX: 8,
            text: `خط مرجع ${instrument.toUpperCase()} (${feedbackNumber(row[key])})`,
            style: { fontFamily: 'YekanBakh, main, sans-serif', fontSize: '11px', color: '#52655d', background: '#f2f6f4' }
          }
        }] : [] }
      }
    },
    async getAssignRecord() {
      this.loadingRecords = true
      this.error = ''
      try {
        const response = await this.requesting('user', 'getAssignRecord')
        this.records = (response && response.data && response.data.assignDoctors) || []
        this.selectedRecord = this.records.length ? this.records[0].id : null
        await this.getChartData()
      } catch (error) { this.error = 'دریافت اطلاعات بازخورد ممکن نشد.' }
      finally { this.loadingRecords = false }
    },
    async getChartData() {
      const requestId = ++this.requestId
      this.sessions = []
      this.error = ''
      this.localLoader = false
      if (!this.selectedRecord) return
      this.localLoader = true
      try {
        const response = await this.requesting('user', 'getMyoutcomeChart', { assignRecordId: this.selectedRecord })
        if (requestId === this.requestId) this.sessions = (response && response.data && response.data.chart_data) || []
      } catch (error) {
        if (requestId === this.requestId) this.error = 'دریافت نمودار ممکن نشد. دوباره تلاش کنید.'
      } finally {
        if (requestId === this.requestId) this.localLoader = false
      }
    }
  }
}
</script>

<style scoped>
.outcome-charts { min-width: 0; }
.outcome-charts__panel { min-width: 0; padding: 20px 14px 8px; border: 1px solid #e2e9e6; border-radius: 16px; background: #fff; }
.outcome-charts .outcome-charts__heading { margin: 0 4px 10px; font-family: 'YekanBakh', main, sans-serif !important; font-size: 17px; font-weight: 700; line-height: 1.9; color: #293b42; }
.outcome-charts__plot { min-width: 0; }
.outcome-charts__caption { margin: 14px 0 0; color: #687b81; font-size: 13px; line-height: 1.9; }
@media (max-width: 600px) {
  .outcome-charts__panel { padding: 16px 6px 4px; }
  .outcome-charts__heading { font-size: 16px; }
}
</style>
